import type { ReactNode } from "react";
import {
  BarChartOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { usePermissions } from "./usePermissions";
import { PERMISSIONS } from "../Utilis/permissions";

export interface HomeShortcut {
  key: string;
  label: string;
  icon: ReactNode;
  to: string;
}

/**
 * The quick links shown on the Home welcome card and in the middle of the
 * header. Shortcuts the user cannot reach are left out rather than disabled,
 * so neither place ever offers a dead end.
 */
export const useHomeShortcuts = (): HomeShortcut[] => {
  const { t } = useTranslation();
  const { canAccessRoute } = usePermissions();

  return [
    {
      key: "orders",
      label: t("home.orders"),
      icon: <ShoppingCartOutlined />,
      permissions: [PERMISSIONS.ORDER.VIEW],
      to: "/orders/list",
    },
    {
      key: "customers",
      label: t("home.customers"),
      icon: <TeamOutlined />,
      permissions: [PERMISSIONS.USER.VIEW],
      to: "/manage-customers",
    },
    {
      key: "reports",
      label: t("home.reports"),
      icon: <BarChartOutlined />,
      // Dashboard is allowed without company_settings; /reports is not.
      permissions: [PERMISSIONS.REPORTS.VIEW],
      to: "/reports/dashboard",
    },
    {
      key: "stores",
      label: t("home.stores"),
      icon: <ShopOutlined />,
      permissions: [PERMISSIONS.COMPANY.SETTINGS],
      // Stores is tab "5" of Company Settings; the page seeds its active tab
      // from this query param.
      to: "/company-setting?tab=5",
    },
  ]
    .filter((shortcut) => canAccessRoute(shortcut.permissions))
    .map(({ permissions, ...shortcut }) => shortcut);
};

export default useHomeShortcuts;
